"use client"

import { observer } from "mobx-react-lite"
import { useEffect, useState, useMemo } from "react"
import { useAppContext } from "../../../shared/functions/Context"
import { dateFormat_YY_MM_DY } from "../../shared/utils/utils"
import {
  type ITemplateRating,
  defaultTemplateRating,
} from "../../../shared/models/three-sixty-feedback-models/TemplateRating"
import DetailView from "./DetailView"
import {
  Archive,
  ArchiveRestore,
  Eye,
  Search,
  Calendar,
  User,
  FileText,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Download,
} from "lucide-react"
import GenerateArchivedRatingsTablePDF from "../ratings/components/GenerateArchivedRatingsTablePDF"

interface IProp {
  data: ITemplateRating[]
  onDataChange: () => void
  showArchivedButton?: boolean
}

const RequestTable = observer(({ data, onDataChange, showArchivedButton = true }: IProp) => {
  const { api, store } = useAppContext()
  const [templateRating, setTemplateRating] = useState<ITemplateRating>({
    ...defaultTemplateRating,
  })

  const users = store.user.all.map((user) => user.asJson)
  const templates = store.templates.all.map((dep) => dep.asJson)

  // State management
  const [searchTerm, setSearchTerm] = useState("")
  const [showCompleted, setShowCompleted] = useState<boolean>(false)
  const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined)
  const [detailViewOpen, setDetailViewOpen] = useState<boolean>(false)
  const [selectedDetail, setSelectedDetail] = useState<ITemplateRating | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [loading, setLoading] = useState(false)

  const getUserName = (userId: string): string => {
    const user = users.find((user) => user.uid === userId)?.displayName
    return user ?? "Unknown User"
  }

  const getTemplateName = (depId: string): string => {
    const template = templates.find((dep) => dep.id === depId)?.title
    return template ?? "Unknown Template"
  }

  const handleArchive = async (rating: ITemplateRating) => {
    const updatedRating = {
      ...rating,
      archived: !rating.archived,
    }
    setTemplateRating(updatedRating)
    setLoading(true)
    try {
      await api.templateRating.update(updatedRating)
      onDataChange()
    } catch (error) {
      console.error("Error updating rating: ", error)
    } finally {
      setLoading(false)
    }
  }

  const handleViewDetails = (row: ITemplateRating) => {
    setSelectedDetail(row)
    setDetailViewOpen(true)
  }

  const handleCloseDetailView = () => {
    setDetailViewOpen(false)
    setSelectedDetail(null)
  }

  // Export functions (placeholder)
  const handleExportPDF = () => {
    const createPDF = GenerateArchivedRatingsTablePDF(data, users)
    createPDF()
  }

  // const createPrintablePage = () => GeneratePrintablePage(data)

  // const handlePrint = () => {
  //   createPrintablePage()
  // }

  useEffect(() => {
    const load = async () => {
      await api.user.getAll()
      await api.templates.getAll()
    }
    load()
  }, [api.user, api.templates])

  // Filter and search data
  const filteredData = data
    .filter((row) => {
      if (selectedYear === undefined) {
        return true
      } else {
        const rowYear = new Date(row.dueDate).getFullYear()
        return rowYear === selectedYear
      }
    })
    .filter((row) => (showCompleted ? row.status === "Completed" : true))
    .filter((row) => {
      if (!searchTerm) return true
      const searchLower = searchTerm.toLowerCase()
      return (
        getUserName(row.rateeId).toLowerCase().includes(searchLower) ||
        getUserName(row.raterId).toLowerCase().includes(searchLower) ||
        getTemplateName(row.templateId).toLowerCase().includes(searchLower) ||
        row.reasonForRequest?.toLowerCase().includes(searchLower) ||
        (row.status?.toLowerCase().includes(searchLower) ?? false)
      )
    })

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage)

  // Get unique years from data
  const uniqueYears = useMemo(() => {
    return Array.from(new Set(data.map((row) => new Date(row.dueDate).getFullYear())))
  }, [data])

  const getStatusColor = (status: string | undefined) => {
    switch (status) {
      case "Completed":
        return "bg-green-100 text-green-800"
      case "In Progress":
        return "bg-blue-100 text-blue-800"
      case "Not Started":
        return "bg-amber-100 text-amber-800"
      default:
        return "bg-slate-100 text-slate-800"
    }
  }

  const getStatusIcon = (status: string | undefined) => {
    switch (status) {
      case "Completed":
        return <CheckCircle2 className="h-3 w-3" />
      case "In Progress":
        return <Clock className="h-3 w-3" />
      case "Not Started":
        return <AlertTriangle className="h-3 w-3" />
      default:
        return <Clock className="h-3 w-3" />
    }
  }

  return (
    <div className="space-y-6">
      {/* Header and Controls */}
      <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search requests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        {/* Filters */}
        <div className="flex gap-3">
          <select
            value={selectedYear ?? ""}
            onChange={(e) => setSelectedYear(e.target.value ? Number(e.target.value) : undefined)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="">All Years</option>
            {uniqueYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          <select
            value={showCompleted ? "completed" : "all"}
            onChange={(e) => setShowCompleted(e.target.value === "completed")}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed Only</option>
          </select>

          {/* Export buttons */}
          <button
            onClick={handleExportPDF}
            className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            title="Export PDF"
          >
            <Download className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Results summary */}
      <div className="flex items-center justify-between text-sm text-slate-600">
        <span>
          Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredData.length)} of {filteredData.length}{" "}
          requests
        </span>
        <select
          value={itemsPerPage}
          onChange={(e) => {
            setItemsPerPage(Number(e.target.value))
            setCurrentPage(1)
          }}
          className="px-2 py-1 border border-slate-300 rounded text-sm"
        >
          <option value={5}>5 per page</option>
          <option value={10}>10 per page</option>
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-lg border border-slate-200/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Ratee
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Rater
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Template
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Heading
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {paginatedData.map((row, index) => (
                <tr
                  key={row.id}
                  className={`hover:bg-slate-50 transition-colors ${index % 2 === 0 ? "bg-white" : "bg-slate-25"}`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      <span className="text-sm text-slate-900">{dateFormat_YY_MM_DY(row.dateRequested ?? 0)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-slate-900">{getUserName(row.rateeId)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-purple-600" />
                      <span className="text-sm font-medium text-slate-900">{getUserName(row.raterId)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-slate-900">{getTemplateName(row.templateId)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-900 max-w-xs truncate" title={row.heading || ""}>
                      {row.heading || "-"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div
                        className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(row.status)}`}
                      >
                        {getStatusIcon(row.status)}
                        {row.status || "Unknown"}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewDetails(row)}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {showArchivedButton && row.status === "Completed" && (
                        <button
                          onClick={() => handleArchive(row)}
                          disabled={loading}
                          className={`p-2 rounded-lg transition-colors ${
                            row.archived
                              ? "text-green-600 hover:text-green-800 hover:bg-green-50"
                              : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                          } disabled:opacity-50`}
                          title={row.archived ? "Unarchive" : "Archive"}
                        >
                          {row.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty state */}
        {paginatedData.length === 0 && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No requests found</h3>
            <p className="text-slate-600">Try adjusting your search or filter criteria.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-600">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {/* Page numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                      currentPage === pageNum
                        ? "bg-purple-600 text-white"
                        : "text-slate-600 hover:text-slate-800 hover:bg-slate-100"
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>

            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Detail View Modal */}
      <DetailView
        open={detailViewOpen}
        onClose={handleCloseDetailView}
        data={selectedDetail}
        getUserName={getUserName}
        getTemplateName={getTemplateName}
      />
    </div>
  )
})

export default RequestTable
